import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  ExportButton,
  TopToolbar,
  SelectInput,
  TextInput,
  useRecordContext,
} from 'react-admin';
import {
  Typography,
  Box,
  Chip,
  Tooltip,
} from '@mui/material';

const webhookFilters = [
  <SelectInput
    key="status"
    source="status"
    label="Durum"
    choices={[
      { id: 'success', name: 'Basarili' },
      { id: 'not_found', name: 'Bulunamadi' },
      { id: 'already_processed', name: 'Zaten Islendi' },
      { id: 'no_code', name: 'Kod Yok' },
    ]}
    alwaysOn
  />,
  <TextInput key="service" source="service" label="Servis" alwaysOn />,
  <TextInput key="activationId" source="activationId" label="Aktivasyon ID" alwaysOn />,
];

const ListActions = () => (
  <TopToolbar>
    <ExportButton
      sx={{
        backgroundColor: '#7367f0',
        color: 'white',
        '&:hover': { backgroundColor: '#5549d9' },
      }}
    />
  </TopToolbar>
);

const StatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  const statusMap: Record<string, { label: string; bg: string; color: string }> = {
    success: { label: 'Basarili', bg: 'rgba(40, 199, 111, 0.12)', color: '#28c76f' },
    not_found: { label: 'Bulunamadi', bg: 'rgba(234, 84, 85, 0.12)', color: '#ea5455' },
    already_processed: { label: 'Zaten Islendi', bg: 'rgba(255, 159, 67, 0.12)', color: '#ff9f43' },
    no_code: { label: 'Kod Yok', bg: 'rgba(0, 207, 232, 0.12)', color: '#00cfe8' },
  };

  const status = statusMap[record.status] || {
    label: record.status,
    bg: 'rgba(103, 109, 125, 0.12)',
    color: '#676d7d',
  };

  return (
    <Chip
      label={status.label}
      size="small"
      sx={{
        backgroundColor: status.bg,
        color: status.color,
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
};

const CodeField = () => {
  const record = useRecordContext();
  if (!record?.code) {
    return (
      <Typography variant="body2" sx={{ color: '#676d7d', fontStyle: 'italic', fontSize: '0.8rem' }}>
        —
      </Typography>
    );
  }

  return (
    <Chip
      label={record.code}
      size="small"
      onClick={() => navigator.clipboard.writeText(record.code)}
      sx={{
        backgroundColor: 'rgba(40, 199, 111, 0.12)',
        color: '#28c76f',
        fontWeight: 700,
        fontFamily: 'monospace',
        fontSize: '0.85rem',
        cursor: 'pointer',
      }}
    />
  );
};

const SmsTextField = () => {
  const record = useRecordContext();
  if (!record?.text) return null;

  const truncated = record.text.length > 50 ? record.text.substring(0, 50) + '...' : record.text;

  return (
    <Tooltip title={record.text} arrow>
      <Typography
        variant="body2"
        sx={{ color: '#d0d2d6', fontSize: '0.8rem', maxWidth: 300, cursor: 'default' }}
      >
        {truncated}
      </Typography>
    </Tooltip>
  );
};

const DateField = () => {
  const record = useRecordContext();
  if (!record?.createdAt) return null;

  const date = new Date(record.createdAt);
  return (
    <Typography variant="body2" sx={{ color: '#d0d2d6', fontSize: '0.8rem' }}>
      {date.toLocaleDateString('tr-TR')} {date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </Typography>
  );
};

const NoteField = () => {
  const record = useRecordContext();
  if (!record?.note) {
    return (
      <Typography variant="body2" sx={{ color: '#676d7d', fontStyle: 'italic', fontSize: '0.8rem' }}>
        —
      </Typography>
    );
  }

  return (
    <Typography variant="body2" sx={{ color: '#d0d2d6', fontSize: '0.8rem' }}>
      {record.note}
    </Typography>
  );
};

export const WebhookLogList = () => (
  <Box>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#d0d2d6' }}>
        Webhook Loglari
      </Typography>
      <Typography variant="body2" sx={{ color: '#676d7d', mt: 0.5 }}>
        HeroSMS'ten gelen webhook isteklerinin kayitlari
      </Typography>
    </Box>

    <List
      resource="admin/sms/webhook-logs"
      filters={webhookFilters}
      actions={<ListActions />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      sx={{
        '& .RaList-main': {
          backgroundColor: '#283046',
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      <Datagrid
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-headerCell': {
            backgroundColor: '#242b3d',
            fontWeight: 600,
            color: '#676d7d',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
          '& .RaDatagrid-row:hover': {
            backgroundColor: '#242b3d',
          },
          '& .RaDatagrid-rowCell': {
            borderColor: '#404656',
          },
        }}
      >
        <TextField source="activationId" label="Aktivasyon ID" />
        <TextField source="service" label="Servis" />
        <FunctionField label="Kod" render={() => <CodeField />} />
        <FunctionField label="SMS Metni" render={() => <SmsTextField />} />
        <FunctionField label="Durum" render={() => <StatusChip />} />
        <FunctionField label="Not" render={() => <NoteField />} />
        <TextField source="country" label="Ulke" />
        <FunctionField label="Tarih" render={() => <DateField />} />
      </Datagrid>
    </List>
  </Box>
);
